import React from 'react'
import {
  ArchiveBulkAction,
  ArchiveBulkWithFiltersAction
} from './archive-action'
import { MergeBulkAction, MergeBulkWithFiltersAction } from './merge-action'
import {
  RestoreBulkAction,
  RestoreBulkWithFiltersAction
} from './restore-action'
import { DeleteBulkAction, DeleteBulkWithFiltersAction } from './delete-action'
import {
  ModifyApplicationTagsAction,
  ModifyApplicationTagsByFilterAction
} from './tag-action'
import { RowSelection } from '../../../legacy-design-system/grid/grid'
import { ActionList } from '../../../legacy-design-system/action'
import { PureQueryOptions } from 'apollo-client'
import { Role, hasRole } from '../../roles'
import { Application, ApplicationAggregationCategory } from '../shared'
import { MetadataFilter } from '../../filter-manager/metadata-filter-manager'
import { useQueryClient } from 'react-query'
import { refreshAndInvalidateQueries } from './shared'
import { invalidateVrLicenseQueries } from '../../value-reporting/modules/licenses/api/queries/vr-licenses'
import { invalidateApplicationModuleQueries } from '../../primary-application/api/get-application-modules'

// TODO Check comment for change here https://bitbucket.org/contrastsecurity/contrast-ui/pull-requests/476/contrast-33505-ui-bug-bulk-actions-are/diff#Lsrc/components/applications-grid/actions/bulk-action-list.tsxT21

interface ActionProps {
  readonly customMetadataSelections: MetadataFilter[]
  readonly organizationRole: Role
  readonly organizationID: string
  readonly isFreemium: boolean
  readonly restrictedEditRole?: boolean
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

export function BulkActionList({
  invertedSelection,
  selectedItems,
  deselectedItems,
  filters: { filterText, quickFilter, aggregations },
  customMetadataSelections,
  organizationRole,
  organizationID,
  isFreemium,
  restrictedEditRole,
  refetchQueries,
  refresh
}: RowSelection<Application, ApplicationAggregationCategory> & ActionProps) {
  const queryClient = useQueryClient()

  function licenseRelatedActionRefresh() {
    return refreshAndInvalidateQueries({
      queryClient,
      invalidate: invalidateVrLicenseQueries,
      refresh
    })
  }

  function applicationModuleRelatedRefresh() {
    return refreshAndInvalidateQueries({
      queryClient,
      invalidate: invalidateApplicationModuleQueries,
      refresh
    })
  }

  const showDisabled = selectedItems
    .concat(deselectedItems)
    .map((app) => app.role)
    .some((role) => hasRole(role, 'EDIT'))

  if (invertedSelection) {
    const filters = Object.assign(
      {},
      { filterText, quickFilter, customMetadata: customMetadataSelections },
      aggregations
    )

    const props = {
      excludedApplications: deselectedItems,
      applications: selectedItems,
      showDisabled,
      filters,
      organizationRole,
      organizationID,
      isFreemium,
      restrictedEditRole,
      refetchQueries,
      refresh
    }

    return (
      <ActionList large>
        <ModifyApplicationTagsByFilterAction bulk {...props} />
        <ArchiveBulkWithFiltersAction
          {...props}
          refresh={licenseRelatedActionRefresh}
        />
        <MergeBulkWithFiltersAction
          {...props}
          refresh={applicationModuleRelatedRefresh}
        />
        <RestoreBulkWithFiltersAction
          {...props}
          refresh={licenseRelatedActionRefresh}
        />
        <DeleteBulkWithFiltersAction
          {...props}
          refresh={licenseRelatedActionRefresh}
        />
      </ActionList>
    )
  }

  const props = {
    applications: selectedItems,
    showDisabled,
    isFreemium,
    organizationRole,
    organizationID,
    restrictedEditRole,
    refetchQueries,
    refresh
  }

  return (
    <ActionList large>
      <ModifyApplicationTagsAction bulk {...props} />
      <ArchiveBulkAction {...props} refresh={licenseRelatedActionRefresh} />
      <MergeBulkAction {...props} refresh={applicationModuleRelatedRefresh} />
      <RestoreBulkAction {...props} refresh={licenseRelatedActionRefresh} />
      <DeleteBulkAction {...props} refresh={licenseRelatedActionRefresh} />
    </ActionList>
  )
}
